import { ACTIVITY_CATEGORIES } from '@yoyoclub/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { discoveryApi, type DiscoveryTopic, type TopicInput } from '../../services/discoveryApi';

const STAGES: [string, string][] = [
  ['s0', '0 to 2'],
  ['s1', '2 to 3'],
  ['s2', '3 to 5'],
  ['s3', '5 to 8'],
  ['s4', '8 and up'],
  ['adults', 'Parents'],
];
const REGIONS: [string, string][] = [
  ['', 'Anywhere'],
  ['SOUTH_BAY', 'South Bay'],
  ['PENINSULA', 'Peninsula'],
  ['EAST_BAY', 'East Bay'],
  ['SAN_FRANCISCO', 'San Francisco'],
  ['OTHER', 'Other'],
];

const blank: TopicInput = { stage: 's2', region: null, category: null, enabled: true, note: null };
const cell = 'rounded-lg border border-gray-300 px-3 py-1.5 text-sm';

function TopicRow({
  value,
  onSave,
  onDelete,
}: {
  value: TopicInput;
  onSave: (t: TopicInput) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const [t, setT] = useState(value);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(t) !== JSON.stringify(value);
  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };
  return (
    <tr>
      <td className="px-4 py-2">
        <select
          className={cell}
          value={t.stage}
          onChange={(e) => setT({ ...t, stage: e.target.value })}
        >
          {STAGES.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </td>
      <td className="px-4 py-2">
        <select
          className={cell}
          value={t.region ?? ''}
          onChange={(e) => setT({ ...t, region: e.target.value || null })}
        >
          {REGIONS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </td>
      <td className="px-4 py-2">
        <select
          className={cell}
          value={t.category ?? ''}
          onChange={(e) => setT({ ...t, category: e.target.value || null })}
        >
          <option value="">Any</option>
          {ACTIVITY_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.labelEN}
            </option>
          ))}
        </select>
      </td>
      <td className="px-4 py-2">
        <input
          className={`${cell} w-full min-w-[200px]`}
          value={t.note ?? ''}
          maxLength={300}
          placeholder="Optional hint for the search"
          onChange={(e) => setT({ ...t, note: e.target.value || null })}
        />
      </td>
      <td className="px-4 py-2">
        <input
          type="checkbox"
          aria-label="Enabled"
          checked={t.enabled}
          onChange={(e) => setT({ ...t, enabled: e.target.checked })}
        />
      </td>
      <td className="whitespace-nowrap px-4 py-2">
        <button
          type="button"
          disabled={busy || (!!onDelete && !dirty)}
          onClick={() => void act(() => onSave(t))}
          className="rounded-lg bg-primary px-3 py-1.5 text-sm text-white disabled:opacity-40"
        >
          {onDelete ? 'Save' : 'Add'}
        </button>
        {onDelete && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void act(onDelete)}
            className="ml-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Delete
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * /admin/picks/topics (PRD §6.4): what the weekly discovery searches for. With no
 * enabled topic, each run covers the two age stages with the fewest activities.
 */
export default function AdminPickTopics() {
  const [topics, setTopics] = useState<DiscoveryTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      setTopics(await discoveryApi.topics());
      setVersion((v) => v + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const guard = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  return (
    <AdminLayout
      title="Discovery topics"
      headerRight={
        <Link
          to="/admin/picks/candidates"
          className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
        >
          Candidates
        </Link>
      }
    >
      <p className="mb-4 text-sm text-gray-600">
        Each weekly run searches every enabled topic, up to 10 candidates in total. With no enabled
        topic it picks the two categories with the fewest activities, courses and picks for
        children, and searches them for every child age.
      </p>
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {loading ? (
        <p className="text-gray-600">Loading...</p>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Ages</th>
                <th className="px-4 py-3">Region</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Note</th>
                <th className="px-4 py-3">On</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {topics.map((t) => {
                const { id, region, stage, category, enabled, note } = t;
                const value = { region, stage, category, enabled, note };
                return (
                  <TopicRow
                    key={`${id}-${version}`}
                    value={value}
                    onSave={(next) => guard(() => discoveryApi.updateTopic(id, next))}
                    onDelete={() => guard(() => discoveryApi.deleteTopic(id))}
                  />
                );
              })}
              <TopicRow
                key={`new-${version}`}
                value={blank}
                onSave={(next) => guard(() => discoveryApi.createTopic(next))}
              />
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
